import { createFileRoute } from '@tanstack/react-router'
import { RouteErrorFallback } from '@/components/ErrorBoundary'
import { useState } from 'react'
import { PageContainer, PageCard, PageHeader, PageHeaderContent, PageHeaderTitle, PageHeaderHeading, PageHeaderDescription, PageHeaderFilters, PageHeaderActions, PageContent } from '@/components/ui/page'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Plus,
  Book,
  Search,
  Edit,
  Eye,
  Trash2,
  Loader2,
  FileText,
  Globe,
  RefreshCw,
} from 'lucide-react'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { TablePagination } from '@/components/ui/table-pagination'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useKnowledgeBase, useKnowledgeBaseNameSearch, useWebsiteSource, useWebsiteSources } from '@/hooks/useKnowledgeBase'
import { useKnowledgeBaseMutations, useWebsiteSourceMutations } from '@/hooks/useKnowledgeBaseMutations'
import { useDebounce } from '@/hooks/useDebounce'
import { EntryDialog } from '@/components/knowledge-base/EntryDialog'
import { EntryViewDialog } from '@/components/knowledge-base/EntryViewDialog'
import { WebsiteSourceStatus } from '@/api/generated/models'
import type { KnowledgeBaseResponseDTO, WebsiteSourceResponseDTO } from '@/api/generated/models'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

export const Route = createFileRoute('/knowledge-base')({
  component: KnowledgeBase,
  errorComponent: RouteErrorFallback,
})

const contentPreview = (content: string) =>
  content
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/[*_`]/g, '')
    .replace(/\s+/g, ' ')
    .trim()

const isSourceActive = (source: WebsiteSourceResponseDTO) =>
  source.status === WebsiteSourceStatus.Queued ||
  source.status === WebsiteSourceStatus.Crawling ||
  source.status === WebsiteSourceStatus.Processing

function SourceStatusBadge({ source }: { source: WebsiteSourceResponseDTO }) {
  switch (source.status) {
    case WebsiteSourceStatus.Queued:
      return <Badge variant="secondary">In Warteschlange</Badge>
    case WebsiteSourceStatus.Crawling:
      return (
        <Badge variant="secondary">
          <Loader2 className="animate-spin" />
          Crawlt…
        </Badge>
      )
    case WebsiteSourceStatus.Processing:
      return (
        <Badge variant="secondary">
          <Loader2 className="animate-spin" />
          Wird verarbeitet…
        </Badge>
      )
    case WebsiteSourceStatus.Ready:
      return <Badge className="border-transparent bg-green-100 text-green-800">Bereit</Badge>
    case WebsiteSourceStatus.Failed:
      if (source.error_message) {
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="destructive">Fehlgeschlagen</Badge>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">{source.error_message}</TooltipContent>
          </Tooltip>
        )
      }
      return <Badge variant="destructive">Fehlgeschlagen</Badge>
  }
}

function KnowledgeBase() {
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create')
  const [selectedEntry, setSelectedEntry] = useState<KnowledgeBaseResponseDTO | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [entryToDelete, setEntryToDelete] = useState<KnowledgeBaseResponseDTO | null>(null)
  const [viewEntry, setViewEntry] = useState<KnowledgeBaseResponseDTO | null>(null)
  const [activeTab, setActiveTab] = useState<'entries' | 'sources'>('entries')
  const [selectedSourceId, setSelectedSourceId] = useState<string | null>(null)
  const [deleteSourceDialogOpen, setDeleteSourceDialogOpen] = useState(false)
  const [sourceToDelete, setSourceToDelete] = useState<WebsiteSourceResponseDTO | null>(null)

  const debouncedSearch = useDebounce(searchTerm, 400)
  const offset = (currentPage - 1) * pageSize

  const { data: allEntries, isLoading: loadingAll, error, refetch } = useKnowledgeBase(pageSize, offset)
  const { data: searchResults, isLoading: loadingSearch } = useKnowledgeBaseNameSearch(
    debouncedSearch,
    pageSize,
    offset
  )

  const entries = (debouncedSearch ? searchResults : allEntries)?.items
  const isLoading = debouncedSearch ? loadingSearch : loadingAll

  const { deleteEntry } = useKnowledgeBaseMutations()
  const {
    data: sources,
    isLoading: sourcesLoading,
    error: sourcesError,
    refetch: refetchSources,
  } = useWebsiteSources(100, 0)
  const { data: sourceDetail, isLoading: sourceDetailLoading } = useWebsiteSource(selectedSourceId)
  const { deleteSource, resyncSource } = useWebsiteSourceMutations()

  const handleCreateNew = () => {
    setDialogMode('create')
    setSelectedEntry(null)
    setDialogOpen(true)
  }

  const handleEdit = (entry: KnowledgeBaseResponseDTO) => {
    setDialogMode('edit')
    setSelectedEntry(entry)
    setDialogOpen(true)
  }

  const handleDeleteClick = (entry: KnowledgeBaseResponseDTO) => {
    setEntryToDelete(entry)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (entryToDelete) {
      try {
        await deleteEntry.mutateAsync(entryToDelete.id)
        setDeleteDialogOpen(false)
        setEntryToDelete(null)
      } catch (err) {
        console.error('Error deleting entry:', err)
      }
    }
  }

  const handleDeleteSourceClick = (source: WebsiteSourceResponseDTO) => {
    setSourceToDelete(source)
    setDeleteSourceDialogOpen(true)
  }

  const handleDeleteSourceConfirm = async () => {
    if (sourceToDelete) {
      try {
        await deleteSource.mutateAsync(sourceToDelete.id)
        setDeleteSourceDialogOpen(false)
        setSourceToDelete(null)
      } catch (err) {
        console.error('Error deleting website source:', err)
      }
    }
  }

  const handleDialogSuccess = () => {
    refetch()
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize)
    setCurrentPage(1)
  }

  const handleSearchChange = (value: string) => {
    setSearchTerm(value)
    setCurrentPage(1)
  }

  const totalItems = (debouncedSearch ? searchResults : allEntries)?.total_count ?? 0

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  return (
    <PageContainer>
      <PageCard>
        <PageHeader>
          <PageHeaderContent>
            <PageHeaderTitle>
              <div className="flex items-center gap-2">
                <Book className="h-5 w-5" />
                <PageHeaderHeading>Wissensdatenbank</PageHeaderHeading>
              </div>
              <PageHeaderDescription>Wissensinhalte für Ihre KI-Agenten</PageHeaderDescription>
            </PageHeaderTitle>
            <PageHeaderFilters>
              {activeTab === 'entries' && (
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Einträge durchsuchen..."
                    value={searchTerm}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="pl-10"
                  />
                </div>
              )}
              <PageHeaderActions>
                <Button onClick={handleCreateNew}>
                  <Plus className="mr-2 h-4 w-4" />
                  Neuer Eintrag
                </Button>
              </PageHeaderActions>
            </PageHeaderFilters>
          </PageHeaderContent>
        </PageHeader>

        <Tabs
          value={activeTab}
          onValueChange={(value) => setActiveTab(value as 'entries' | 'sources')}
          className="px-6 pt-4"
        >
          <TabsList>
            <TabsTrigger value="entries">Einträge</TabsTrigger>
            <TabsTrigger value="sources">Website-Quellen</TabsTrigger>
          </TabsList>
        </Tabs>

        {activeTab === 'entries' && (
          <>
          <PageContent className="p-6 overflow-auto">
            {isLoading && (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            )}

            {error && (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <FileText className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">
                  Fehler beim Laden der Einträge
                </p>
                <Button variant="outline" className="mt-4" onClick={() => refetch()}>
                  Erneut versuchen
                </Button>
              </div>
            )}

            {!isLoading && !error && entries?.length === 0 && (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <Book className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-lg font-semibold">Keine Einträge gefunden</p>
                <p className="text-muted-foreground mt-2">
                  {searchTerm
                    ? 'Keine Einträge entsprechen Ihrer Suche'
                    : 'Erstellen Sie Ihren ersten Wissenseintrag'}
                </p>
                {!searchTerm && (
                  <Button className="mt-4" onClick={handleCreateNew}>
                    <Plus className="mr-2 h-4 w-4" />
                    Ersten Eintrag erstellen
                  </Button>
                )}
              </div>
            )}

            {!isLoading && !error && entries && entries.length > 0 && (
              // Mindestbreite statt gequetschter Spalten: `table-fixed` + `w-full`
              // wuerde den `overflow-x-auto`-Wrapper aus table.tsx aushebeln.
              <Table className="table-fixed min-w-[720px] md:min-w-0">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[30%]">Name</TableHead>
                    <TableHead>Inhalt</TableHead>
                    <TableHead className="w-[110px]">Quelle</TableHead>
                    <TableHead className="w-[120px]">Tags</TableHead>
                    <TableHead className="w-[120px]">Erstellt am</TableHead>
                    <TableHead className="w-[100px]">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell className="font-medium truncate">{entry.name}</TableCell>
                      <TableCell>
                        <p className="text-sm text-muted-foreground truncate">
                          {contentPreview(entry.content)}
                        </p>
                      </TableCell>
                      <TableCell>
                        {entry.source_url ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <a
                                href={entry.source_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:underline"
                              >
                                <Globe className="h-3.5 w-3.5 shrink-0" />
                                Website
                              </a>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-[80vw] break-all">
                              {entry.source_url.replace(/^https?:\/\/(www\.)?/, '')}
                            </TooltipContent>
                          </Tooltip>
                        ) : entry.source_id ? (
                          <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                            <Globe className="h-3.5 w-3.5 shrink-0" />
                            Website
                          </span>
                        ) : (
                          <span className="text-sm text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {entry.tags && entry.tags.length > 0 && (
                          <div className="flex items-center gap-1 overflow-hidden">
                            <Badge variant="secondary" className="text-xs min-w-0 shrink">
                              <span className="truncate">{entry.tags[0]}</span>
                            </Badge>
                            {entry.tags.length > 1 && (
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Badge
                                    variant="secondary"
                                    className="text-xs cursor-pointer"
                                    role="button"
                                    tabIndex={0}
                                  >
                                    +{entry.tags.length - 1}
                                  </Badge>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto max-w-72 p-2" align="start">
                                  <div className="flex flex-wrap gap-1">
                                    {entry.tags.map((tag, index) => (
                                      <Badge key={index} variant="secondary" className="text-xs">
                                        {tag}
                                      </Badge>
                                    ))}
                                  </div>
                                </PopoverContent>
                              </Popover>
                            )}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {formatDate(entry.created_at)}
                      </TableCell>
                      <TableCell>
                        {entry.source_id ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setViewEntry(entry)}
                            title="Ansehen"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        ) : (
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(entry)}
                              title="Bearbeiten"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteClick(entry)}
                              title="Löschen"
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </PageContent>

          {!isLoading && !error && entries && entries.length > 0 && (
            <TablePagination
              currentPage={currentPage}
              totalPages={Math.max(1, Math.ceil(totalItems / pageSize))}
              totalItems={totalItems}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
          </>
        )}

        {activeTab === 'sources' && (
          <PageContent className="p-6 overflow-auto">
            {sourcesLoading && (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            )}

            {sourcesError && (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <FileText className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">
                  Fehler beim Laden der Website-Quellen
                </p>
                <Button variant="outline" className="mt-4" onClick={() => refetchSources()}>
                  Erneut versuchen
                </Button>
              </div>
            )}

            {!sourcesLoading && !sourcesError && sources?.length === 0 && (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <Globe className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-lg font-semibold">Noch keine Website-Quellen</p>
                <p className="text-muted-foreground mt-2">
                  Nutzen Sie „Neuer Eintrag" → „Website analysieren", um eine Website zu crawlen.
                </p>
              </div>
            )}

            {!sourcesLoading && !sourcesError && sources && sources.length > 0 && (
              // Sechs Spalten passen unter md nicht nebeneinander — lieber scrollen.
              <Table className="min-w-[720px] md:min-w-0">
                <TableHeader>
                  <TableRow>
                    <TableHead>Website</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Einträge</TableHead>
                    <TableHead>Seiten</TableHead>
                    <TableHead>Letzter Sync</TableHead>
                    <TableHead className="w-[100px]">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sources.map((source) => (
                    <TableRow
                      key={source.id}
                      className="cursor-pointer"
                      onClick={() => setSelectedSourceId(source.id)}
                    >
                      <TableCell className="max-w-[300px]">
                        <p className="font-medium truncate">{source.name || source.url}</p>
                        {source.name && (
                          <p className="text-xs text-muted-foreground truncate">{source.url}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        <SourceStatusBadge source={source} />
                      </TableCell>
                      <TableCell>{source.entry_count}</TableCell>
                      <TableCell>{source.pages_crawled}</TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {source.last_synced_at ? formatDate(source.last_synced_at) : '—'}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation()
                              resyncSource.mutate(source.id)
                            }}
                            disabled={isSourceActive(source) || resyncSource.isPending}
                            title="Neu synchronisieren"
                          >
                            <RefreshCw className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDeleteSourceClick(source)
                            }}
                            title="Quelle löschen"
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </PageContent>
        )}
      </PageCard>

      {/* Entry Dialog */}
      <EntryDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        mode={dialogMode}
        entry={selectedEntry}
        onSuccess={handleDialogSuccess}
        onWebsiteSourceCreated={() => setActiveTab('sources')}
      />

      {/* Entry View Dialog */}
      <EntryViewDialog
        entry={viewEntry}
        open={!!viewEntry}
        onOpenChange={(open) => {
          if (!open) setViewEntry(null)
        }}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eintrag löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie den Eintrag "{entryToDelete?.name}" wirklich löschen? Diese Aktion kann
              nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive hover:bg-destructive/90"
            >
              {deleteEntry.isPending ? 'Wird gelöscht...' : 'Löschen'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Website Source Dialog */}
      <AlertDialog open={deleteSourceDialogOpen} onOpenChange={setDeleteSourceDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Website-Quelle löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie die Quelle "{sourceToDelete?.name || sourceToDelete?.url}" wirklich
              löschen? Dabei werden auch alle {sourceToDelete?.entry_count} automatisch erstellten
              Einträge gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSourceConfirm}
              className="bg-destructive hover:bg-destructive/90"
            >
              {deleteSource.isPending ? 'Wird gelöscht...' : 'Löschen'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Website Source Detail Sheet */}
      <Sheet
        open={selectedSourceId !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedSourceId(null)
        }}
      >
        <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2 pr-6">
              <Globe className="h-4 w-4 shrink-0" />
              <span className="truncate">
                {sourceDetail ? sourceDetail.name || sourceDetail.url : 'Website-Quelle'}
              </span>
            </SheetTitle>
            {sourceDetail && (
              <>
                <SheetDescription className="truncate">{sourceDetail.url}</SheetDescription>
                <div>
                  <SourceStatusBadge source={sourceDetail} />
                </div>
              </>
            )}
          </SheetHeader>
          {sourceDetailLoading && (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}
          {sourceDetail && (
            <div className="space-y-3 px-4 pb-4">
              {sourceDetail.entries.map((entry) => (
                <div
                  key={entry.id}
                  className="rounded-md border p-3 cursor-pointer hover:bg-muted/50"
                  onClick={() => setViewEntry(entry)}
                >
                  <p className="text-sm font-bold">{entry.name}</p>
                  {entry.source_url && (
                    <a
                      href={entry.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="block truncate text-xs text-muted-foreground hover:underline"
                    >
                      {entry.source_url}
                    </a>
                  )}
                  <p className="text-sm text-muted-foreground line-clamp-3 break-words">
                    {contentPreview(entry.content)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </PageContainer>
  )
}
